/**
 * Feuille de brouillon LIBRE — pour calculer, sans HORA.
 *
 * En 1re, une question qui n'a pas de feuille à elle (pas de modèle publié)
 * reçoit quand même une feuille : l'élève y pose ses calculs, le moteur vérifie
 * ceux qui ne portent que sur des nombres (« 15 × 34 = 510 » juste,
 * « 2 + 3 = 6 » faux), et une ligne avec des lettres se garde sans vérification.
 * Aucun HORA, aucun coup de pouce (il n'y a pas de raisonnement attendu).
 *
 * Rien n'est écrit en base (`record_scratch_line` exige une feuille publiée) :
 * les lignes sont gardées sur l'appareil, par tentative et par question.
 *
 * Pur, sauf le petit stockage local (try/catch : jamais d'erreur remontée).
 */

import {
  OP_ORDER,
  chipKind,
  type Palette,
  type PaletteChip,
  type ScratchModel,
} from '@jeraisonne/reasoner';
import type { ViewVerdict } from './scratch-session';

/** La mention affichée sur une feuille libre (carte de la question et feuille ouverte). */
export const FREE_SHEET_NOTE = 'Feuille pour calculer : pas d’HORA à gagner sur cette question.';

/** Niveaux où une question sans feuille reçoit une feuille libre. */
export const FREE_SHEET_LEVELS: readonly string[] = ['1re'];

export function isFreeSheetLevel(level: string | null | undefined): boolean {
  return !!level && FREE_SHEET_LEVELS.includes(level);
}

/** Modèle minimal : une grandeur cherchée sans valeur, donc aucun pas « utile » ni HORA. */
export const FREE_MODEL: ScratchModel = { symbols: { R: {} }, goal: ['R'] };

/** Les opérations d'une feuille libre (ordre de la palette du moteur). */
const FREE_OPS = new Set(['+', '−', '×', '÷', '(', ')', '²', '^', '√', 'ln', 'exp', 'π', '=']);

/** Les touches du pavé numérique. */
export const FREE_DIGITS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '0', ','] as const;

/** Nombres écrits dans le texte d'une question (« 3.5 » devient « 3,5 »), sans doublon, 16 au plus. */
export function numbersInText(text: string): string[] {
  const seen = new Set<string>();
  const out: { s: string; v: number }[] = [];
  for (const m of text.matchAll(/\d+(?:[.,]\d+)?/g)) {
    const s = m[0].replace('.', ',').replace(/^0+(?=\d)/, '');
    if (seen.has(s)) continue;
    seen.add(s);
    out.push({ s, v: Number(s.replace(',', '.')) });
    if (out.length >= 16) break;
  }
  return out.sort((a, b) => a.v - b.v).map((n) => n.s);
}

/** Champs de texte d'un bloc d'énoncé ou de question (les visuels ne comptent pas). */
const TEXT_FIELDS = ['context_md', 'statement_md', 'markdown', 'sub_statement_md', 'prompt_md'];

/** Les nombres de l'énoncé d'une question (ses sous-énoncés puis la question elle-même). */
export function numbersFromBlocks(blocks: { data?: unknown }[]): string[] {
  const text = blocks
    .map((b) => {
      const data = (b.data ?? {}) as Record<string, unknown>;
      return TEXT_FIELDS.map((f) => (typeof data[f] === 'string' ? (data[f] as string) : '')).join(
        ' ',
      );
    })
    .join(' ');
  // factorielles de l'énoncé (« 8! », « 6! ») : un bout chacune, et la touche « ! »
  const facts = [...new Set([...text.matchAll(/(\d+)\s*!/g)].map((m) => `${m[1]}!`))];
  return [...numbersInText(text), ...facts];
}

function chip(atoms: string[]): PaletteChip {
  return { atoms, text: atoms.join(' '), kind: chipKind(atoms) };
}

/** Palette d'une feuille libre : les nombres de la question et les opérations usuelles. */
export function freePalette(numbers: string[]): Palette {
  const facts = numbers.some((n) => n.endsWith('!'));
  return {
    statement: numbers.map((n) => (n.endsWith('!') ? chip([n.slice(0, -1), '!']) : chip([n]))),
    answers: [],
    values: [],
    ops: OP_ORDER.filter((o) => FREE_OPS.has(o) || (facts && o === '!')),
  };
}

/**
 * Le verdict du moteur, lu comme sur une feuille libre : un calcul juste se
 * garde (jamais validé avec HORA), une ligne que le moteur ne sait pas
 * justifier (elle contient des lettres) se garde sans vérification.
 */
export function freeVerdict(v: ViewVerdict): ViewVerdict {
  switch (v.kind) {
    case 'step':
    case 'ready':
    case 'useless':
      return {
        ...v,
        kind: 'useless',
        hora: 0,
        title: 'Calcul juste',
        message: 'Garde-le sur ta feuille s’il t’aide.',
      };
    case 'unjustified':
      return {
        ...v,
        kind: 'useless',
        hora: 0,
        unchecked: true,
        title: 'Ligne non vérifiée',
        message:
          'Ici, je vérifie seulement les calculs avec des nombres. Tu peux garder cette ligne sur ta feuille.',
      };
    default:
      return v;
  }
}

/** Un nombre en cours de saisie (« 3, ») : la ligne n'est pas encore jugeable. */
export function endsWithOpenDecimal(atoms: string[]): boolean {
  const last = atoms[atoms.length - 1];
  return !!last && /^\d+,$/.test(last);
}

/** Nouveau texte d'un nombre après une touche du pavé, ou null s'il faut un nouveau bout. */
export function appendDigit(current: string, d: string): string | null {
  if (!/^\d+(,\d*)?$/.test(current)) return null;
  if (d === ',') return current.includes(',') ? current : `${current},`;
  if (current === '0') return d;
  return current + d;
}

// ── lignes gardées sur l'appareil ───────────────────────────────────────────

export interface StoredFreeLine {
  atoms: string[];
  unchecked: boolean;
}

const PREFIX = 'jr_free_scratch:';

export function freeStorageKey(attemptId: string | null, questionId: string): string | null {
  return attemptId ? `${PREFIX}${attemptId}:${questionId}` : null;
}

export function loadFreeLines(key: string | null): StoredFreeLine[] {
  if (!key) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((l) => {
      const atoms = (l as { atoms?: unknown }).atoms;
      if (!Array.isArray(atoms) || !atoms.every((a) => typeof a === 'string') || !atoms.length)
        return [];
      return [
        { atoms: atoms as string[], unchecked: (l as { unchecked?: unknown }).unchecked === true },
      ];
    });
  } catch {
    return [];
  }
}

export function saveFreeLines(key: string | null, lines: StoredFreeLine[]): void {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(lines.slice(0, 60)));
  } catch {
    /* stockage indisponible : la feuille vit le temps de la page */
  }
}

/** Ligne d'une feuille libre du moteur v2 : son texte, tel que le moteur l'a jugé. */
export interface StoredFreeTextLine {
  text: string;
}

/** Les lignes d'une feuille libre v2 gardées sur l'appareil (clé de la feuille libre + « :v2 »). */
export function loadFreeTextLines(key: string | null): StoredFreeTextLine[] {
  if (!key) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(`${key}:v2`) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((l) => {
      const text = (l as { text?: unknown }).text;
      return typeof text === 'string' && text ? [{ text }] : [];
    });
  } catch {
    return [];
  }
}

export function saveFreeTextLines(key: string | null, lines: StoredFreeTextLine[]): void {
  if (!key) return;
  try {
    localStorage.setItem(`${key}:v2`, JSON.stringify(lines.slice(0, 60)));
  } catch {
    /* stockage indisponible : la feuille vit le temps de la page */
  }
}

/**
 * Ce que la question écrit, pour construire sa feuille libre du moteur v2 (`freeV2Model`) : son énoncé (sous-énoncés
 * puis question) et les propositions de sa première étape.
 */
export function freeModelInputOf(
  questionBlocks: { data?: unknown }[],
  choiceBlocks: { data?: unknown }[],
  level: string | null,
): { promptMd: string; choices: string[]; level: string | null } {
  const promptMd = questionBlocks
    .map((b) => {
      const data = (b.data ?? {}) as Record<string, unknown>;
      return TEXT_FIELDS.map((f) => (typeof data[f] === 'string' ? (data[f] as string) : ''))
        .filter(Boolean)
        .join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n');
  const choices = choiceBlocks.flatMap((b) => {
    const data = (b.data ?? {}) as Record<string, unknown>;
    const t = data.answer_md ?? data.label_md ?? data.markdown;
    return typeof t === 'string' && t ? [t] : [];
  });
  return { promptMd, choices, level };
}
